import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { filePresenceQueryKeys } from '@/hooks/files/types'

import type { FilePresence } from '@/types/file/FilePresence'
import type { UUID } from '@/types/Id'

import { createFilePresenceLoader } from '@/services/file-presence-loader'
import DataLoader from '@/vendor/dataloader'

/**
 * How often a screen showing presence asks again. Closer buys little: the
 * collaboration server only counts a leave once the socket closes, so a few
 * seconds' lag is the floor whatever this is set to.
 */
export const PRESENCE_POLL_MS = 15 * 1000

// Mounted before there is an access token to ask with, the context is null.
// Missing entirely it is undefined, which the hook below refuses.
type LoaderContextType =
  | DataLoader<UUID, FilePresence | null>
  | null
  | undefined

const LoaderContext = createContext<LoaderContextType>(undefined)

interface FilePresenceLoaderProviderProps {
  children: React.ReactNode
}

export const FilePresenceLoaderProvider: React.FC<
  FilePresenceLoaderProviderProps
> = ({ children }) => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const loader = useMemo(() => {
    return accessToken ? createFilePresenceLoader(accessToken) : null
  }, [accessToken])

  // One timer for the whole screen rather than one per file. A long list mounts
  // its rows as they are scrolled to, and a file asking again on its own timer
  // asks on whatever schedule its row started on — too far apart for the loader
  // to gather. Asking for them all in the same tick is one request. A hidden tab
  // is left alone, and comes back up to date on its own: returning to it refetches
  // once on focus.
  useEffect(() => {
    const poll = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return
      }

      void queryClient.invalidateQueries({
        queryKey: filePresenceQueryKeys.all(),
      })
    }, PRESENCE_POLL_MS)

    return () => clearInterval(poll)
  }, [queryClient])

  return (
    <LoaderContext.Provider value={loader}>{children}</LoaderContext.Provider>
  )
}

export const useFilePresenceLoader = () => {
  const context = useContext(LoaderContext)

  if (context === undefined) {
    throw new Error(
      'useFilePresenceLoader must be used within a FilePresenceLoaderProvider',
    )
  }

  return context
}
